import { motion } from 'framer-motion';
import { MapPin, Crown, Sparkles } from 'lucide-react';

export function BlogCoverGoogleMaps() {
  // Connection points converging to center
  const points = [
    { x: 15, y: 20, delay: 0 },
    { x: 85, y: 25, delay: 0.1 },
    { x: 10, y: 70, delay: 0.2 },
    { x: 90, y: 75, delay: 0.15 },
    { x: 30, y: 15, delay: 0.25 },
    { x: 70, y: 85, delay: 0.3 },
    { x: 20, y: 45, delay: 0.35 },
    { x: 80, y: 50, delay: 0.4 },
  ];

  return (
    <div className="w-full h-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center overflow-hidden relative">
      {/* Stylized map background */}
      <div className="absolute inset-0 opacity-20">
        {/* Roads/streets pattern */}
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="roads" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              <path d="M0 50 L100 50" stroke="rgba(255,255,255,0.1)" strokeWidth="2" />
              <path d="M50 0 L50 100" stroke="rgba(255,255,255,0.1)" strokeWidth="2" />
              <path d="M0 25 L100 25" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
              <path d="M0 75 L100 75" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
              <path d="M25 0 L25 100" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
              <path d="M75 0 L75 100" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#roads)" />
        </svg>
      </div>
      
      {/* Central glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-primary/30 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-yellow-400/20 rounded-full blur-2xl" />
      
      {/* Converging lines to center */}
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
        {points.map((point, i) => (
          <motion.line
            key={i}
            x1={`${point.x}%`}
            y1={`${point.y}%`}
            x2="50%"
            y2="50%"
            stroke="url(#lineGradient)"
            strokeWidth="1"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.6 }}
            transition={{ duration: 1, delay: point.delay }}
          />
        ))}
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(139, 92, 246, 0.3)" />
            <stop offset="100%" stopColor="rgba(139, 92, 246, 0.8)" />
          </linearGradient>
        </defs>
      </svg>
      
      {/* Outer points (competitors/other locations) */}
      {points.map((point, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{ left: `${point.x}%`, top: `${point.y}%` }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: point.delay + 0.2, duration: 0.3 }}
        >
          <div className="relative -translate-x-1/2 -translate-y-1/2">
            <div className="w-2 h-2 bg-slate-500 rounded-full" />
            <div className="absolute inset-0 w-2 h-2 bg-slate-500 rounded-full animate-ping opacity-30" />
          </div>
        </motion.div>
      ))}
      
      {/* Central dominant pin (client's business) */}
      <motion.div 
        className="relative z-10"
        initial={{ scale: 0, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5, type: "spring" }}
      >
        {/* Pulse rings */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 border border-primary/30 rounded-full animate-ping" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 border border-primary/40 rounded-full animate-pulse" />
        
        {/* Main pin */}
        <div className="relative">
          <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-primary to-primary/80 rounded-full flex items-center justify-center shadow-lg shadow-primary/50 border-2 border-white/20">
            <MapPin className="w-6 h-6 md:w-7 md:h-7 text-white" />
          </div>
          
          {/* Crown badge */}
          <motion.div 
            className="absolute -top-3 -right-2"
            initial={{ rotate: -20, scale: 0 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{ delay: 0.8, duration: 0.3, type: "spring" }}
          >
            <div className="w-6 h-6 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg">
              <Crown className="w-3.5 h-3.5 text-white" />
            </div>
          </motion.div>
        </div>
        
        {/* Label */}
        <motion.div 
          className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.3 }}
        >
          <div className="flex items-center gap-1 bg-slate-800/90 backdrop-blur-sm px-2 py-1 rounded-full border border-slate-700/50">
            <Sparkles className="w-3 h-3 text-yellow-400" />
            <span className="text-[9px] text-white font-medium">#1 Local</span>
          </div>
        </motion.div>
      </motion.div>
      
      {/* Corner decorations */}
      <div className="absolute top-4 right-4 flex items-center gap-1 bg-slate-800/60 backdrop-blur-sm px-2 py-1 rounded-full border border-slate-700/50">
        <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
        <span className="text-[8px] text-slate-400">Live</span>
      </div>
      
      {/* Pack Local indicator */}
      <div className="absolute bottom-4 left-4 bg-slate-800/60 backdrop-blur-sm px-2 py-1 rounded-lg border border-slate-700/50">
        <span className="text-[8px] text-slate-400">Pack Local</span>
        <div className="flex items-center gap-0.5 mt-0.5">
          <div className="w-4 h-1 bg-primary rounded-full" />
          <div className="w-3 h-1 bg-slate-600 rounded-full" />
          <div className="w-3 h-1 bg-slate-600 rounded-full" />
        </div>
      </div>
    </div>
  );
}
